import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';
import { useApi, useConsulta } from '@plataforma/web/api';
import { mxn } from '../formato';
import { ESTADO_PIEZA, type Pieza } from '../paginas/Piezas';
import { usePuede } from '../permisos';

/** Piezas del vehículo (yonke): desarme por la plantilla del catálogo o una por una. */
export function PiezasVehiculo({ vehiculoId }: { vehiculoId: string }) {
  const api = useApi();
  const permitido = usePuede();
  const piezas = useConsulta<Pieza[]>(`/inventory/vehiculos/${vehiculoId}/piezas`);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const administra = permitido('piezas.administrar');

  const accion = async (fn: () => Promise<unknown>) => {
    setError(null);
    setOcupado(true);
    try {
      await fn();
      piezas.recargar();
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setOcupado(false);
    }
  };
  const agregar = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const forma = e.currentTarget;
    const d = new FormData(forma);
    const ok = await accion(() =>
      api('POST', `/inventory/vehiculos/${vehiculoId}/piezas`, {
        nombre: d.get('nombre'),
        categoria: d.get('categoria'),
        precioListaMxn: d.get('precio'),
        ...(d.get('numeroParte') && { numeroParte: d.get('numeroParte') }),
        ...(d.get('grado') && { grado: d.get('grado') }),
      }),
    );
    if (ok) forma.reset();
  };

  const lista = piezas.datos ?? [];
  if (!administra && !lista.length) return null;
  return (
    <section className="seccion">
      <header>
        <h2>Piezas</h2>
        <div style={{ flex: 1 }} />
        {administra && !lista.length && (
          <button
            className="boton"
            disabled={ocupado}
            onClick={() =>
              void accion(() =>
                api('POST', `/inventory/vehiculos/${vehiculoId}/piezas/desde-plantilla`),
              )
            }
          >
            Desarmar con la plantilla
          </button>
        )}
      </header>
      {error && <p className="error">{error}</p>}
      {lista.length > 0 && (
        <table>
          <tbody>
            {lista.map((p) => (
              <tr key={p.id}>
                <td>
                  {p.nombre}
                  <div className="vin">
                    {p.codigo} · {p.categoria}
                  </div>
                </td>
                <td>
                  <span className="etiqueta">{ESTADO_PIEZA[p.estado] ?? p.estado}</span>
                </td>
                <td>{p.ubicacion ?? '—'}</td>
                <td className="num">{mxn(p.precioListaMxn)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {lista.length > 0 && (
        <p className="cuerpo subtitulo">
          Cambia su estado, ubícalas o imprime su QR en <Link to="/piezas">Piezas</Link>.
        </p>
      )}
      {administra && (
        <form
          className="cuerpo formulario"
          onSubmit={(e) => void agregar(e)}
          style={{ borderTop: '1px solid var(--borde-suave)' }}
        >
          <label className="campo">
            <span>Pieza</span>
            <input name="nombre" required minLength={2} maxLength={120} />
          </label>
          <label className="campo">
            <span>Categoría</span>
            <input
              name="categoria"
              required
              minLength={2}
              maxLength={60}
              placeholder="Carrocería"
            />
          </label>
          <label className="campo">
            <span>Número de parte</span>
            <input name="numeroParte" maxLength={60} />
          </label>
          <label className="campo">
            <span>Grado</span>
            <select name="grado" defaultValue="">
              <option value="">—</option>
              <option value="A">A</option>
              <option value="B">B</option>
              <option value="C">C</option>
            </select>
          </label>
          <label className="campo">
            <span>Precio de lista (MXN)</span>
            <input name="precio" required inputMode="decimal" pattern="\d{1,10}(\.\d{1,2})?" />
          </label>
          <div>
            <button className="boton" disabled={ocupado}>
              Agregar pieza
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
